/**
 * ChatWorkspace Component
 * Main chat area with messages, stages, and graph visualization
 */

import React, { useRef, useEffect } from 'react';
import CommandDeck from '@stepflow/ui-shared/components/CommandDeck.jsx';
import DeckLeftGroup from '@stepflow/ui-shared/components/DeckLeftGroup.jsx';
import { useMarkdownField } from '@stepflow/ui-shared/hooks/useMarkdownField.js';
import WorkbenchPromptAlertBand from '@stepflow/ui-shared/components/WorkbenchPromptAlertBand.jsx';
import { useWorkbenchPromptFieldHeight } from '@/hooks/useWorkbenchPromptFieldHeight.js';
import {
  getWorkbenchPromptInputClassName,
  WORKBENCH_PROMPT_FIELD_STYLE,
  WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING,
} from '@/utils/workbenchPromptFieldClassNames.js';
import StageRenderer from './StageRenderer';
import BuildActivityPanel from './BuildActivityPanel.jsx';
import { formatMessageTime, formatMessageTimeFull } from '@stepflow/ui-shared/utils/formatDate.js';

const BUILD_CHAT_PROMPT_TIP_KEY = 'stepflow:build-chat-prompt-tip-dismissed';

const ChatWorkspace = ({
  network,
  messages,
  stages,
  activeStage,
  artifact,
  onSendMessage,
  isProcessing,
  // Approval handlers
  showApprovalBar,
  onApprove,
  onDeny,
  onModify,
  inputDisabled,
  inputRef: externalInputRef,
  approvalLoadError,
  onRetryApprovalLoad,
  // Optional control rendered in the composer's bottom-left action bar
  // (e.g. the "Generate network skills" toggle from BuildTab).
  composerBottomLeft = null,
  // THE ROOM'S THREE-DECK COMPOSER (MODES.md §9): the "+" menu sits TOP-LEFT. ``composerTopLeft`` wins; the old
  // ``composerBottomLeft`` prop still lands there (same control, moved).
  composerTopLeft = null,
  // the upper deck's RIGHT (the Revise actions)
  composerTopRight = null,
  // the lower deck's LEFT (opposite Send): the build's on/off choices as switches
  composerLowerLeft = null,
  // Ephemeral build-activity slice (agent/skill rows) — shown during a build.
  buildActivity = null,
  // One-shot composer prefill (e.g. the self-improvement revise prompt). When this
  // changes to a non-empty value we drop it into the input for the user to review/send.
  seedText = '',
  // A confirmation for the composer's banner (global UX: confirmations sit on the banner above the prompt box) —
  // wins over the tip; the build's own approval band still wins over it.
  alertBandOverride = null,
}) => {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const internalInputRef = useRef(null);
  const inputRef = externalInputRef != null ? externalInputRef : internalInputRef;
  const [inputValue, setInputValue] = React.useState('');

  // Prefill the composer when a seed prompt arrives (self-improvement §6b handoff).
  React.useEffect(() => {
    const t = String(seedText || '').trim();
    if (!t) return;
    setInputValue(t);
    try { inputRef.current?.focus(); } catch { /* noop */ }
  }, [seedText]); // eslint-disable-line react-hooks/exhaustive-deps
  const [buildPromptTipDismissed, setBuildPromptTipDismissed] = React.useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(BUILD_CHAT_PROMPT_TIP_KEY) === '1';
    } catch {
      return false;
    }
  });

  // EVERY user prompt for this build, in order: the first (kickoff) + each
  // revise/update stacked after it. ALL of them live in the build deck — none
  // ever render as a standalone bubble (the single-deck goal). On a revise there
  // are 2+ user messages; suppressing only the last one (the old bug) leaked the
  // original back as an old-style bubble.
  const buildPrompts = React.useMemo(
    () => (Array.isArray(messages) ? messages.filter((m) => m && m.role === 'user') : []),
    [messages],
  );
  // First prompt drives panel visibility + the deck header.
  const buildPrompt = buildPrompts[0] || null;

  // ONE window, ONE style — no old bubble. The build box is the single home for
  // the prompt + activity. It mounts whenever there is anything to show: a build
  // prompt at all, an active/paused build, or persisted rows. Crucially this is
  // driven by *presence of a prompt*, not by transient flags (isProcessing /
  // showApprovalBar) — so the box never unmounts at a state transition (pre-
  // first-event, approval pause, done) and the standalone bubble never flashes
  // back in. The panel renders its own header (spinner / paused / built) for
  // each state internally, so the OUTER frame is stable across the whole flow.
  const buildPanelVisible = !!buildPrompt
    || isProcessing
    || showApprovalBar
    || (!!buildActivity
      && ((buildActivity.agents && Object.keys(buildActivity.agents).length > 0)
        || (buildActivity.skills && Object.keys(buildActivity.skills).length > 0)
        // Persisted architect reasoning alone keeps the panel visible on reopen, so a
        // user can always read WHY the network was designed — even with no agent/skill rows.
        || (typeof buildActivity.reasoning === 'string' && buildActivity.reasoning.length > 0)));

  // Auto-scroll to bottom whenever the stream height changes — pin newest
  // content just above the fixed composer. Scrolling the inner container
  // directly avoids the window-scroll fallback that ``scrollIntoView`` hits
  // when the nearest scroll ancestor isn't the viewport.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, stages, artifact, isProcessing, activeStage]);

  useWorkbenchPromptFieldHeight(inputRef, inputValue, { maxHeightPx: 200 });   // auto-grows the field (≤ 200px)

  const placeholderText = inputDisabled
    ? 'Waiting for approval...'
    : 'Describe your agentic network requirements…';
  // THE ROOM'S PROMPT FIELD, EXACTLY (owner 2026-10-22: 100% parity) — a plain textarea whose text is MARKDOWN; the
  // format buttons + ⌘B / ⌘I / ⌘⇧7 / ⌘⇧8 + Shift+Enter list continuation are the room's own (textFormat.js).
  const { format: formatInput, handleFormatKey } = useMarkdownField(inputValue, setInputValue, inputRef);
  const send = () => {
    if (!inputValue.trim() || isProcessing) return;
    onSendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (handleFormatKey(e)) return;              // ⌘B / ⌘I / ⌘⇧7 / ⌘⇧8, Shift+Enter list continuation (the room's)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const dismissBuildPromptTip = () => {
    try {
      sessionStorage.setItem(BUILD_CHAT_PROMPT_TIP_KEY, '1');
    } catch {
      /* ignore */
    }
    setBuildPromptTipDismissed(true);
  };

  const approvalAlertBand = showApprovalBar ? (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="min-w-0 flex-1 text-sm font-medium text-gray-700">
        Review the architecture above and decide:
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onApprove}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          Approve
        </button>
        <button
          type="button"
          onClick={onModify}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Modify
        </button>
        <button
          type="button"
          onClick={onDeny}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Deny
        </button>
      </span>
    </div>
  ) : null;

  const buildPromptAlertBand = approvalAlertBand ?? alertBandOverride ?? (
    buildPromptTipDismissed ? null : (
      <WorkbenchPromptAlertBand
        message={
          <>
            Describe your network in plain language. The build assistant streams updates above—keep this tab open while it works.
          </>
        }
        primaryLabel="Got it"
        onPrimary={dismissBuildPromptTip}
        onDismiss={dismissBuildPromptTip}
      />
    )
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">{/* white, like the room (no grey wash) */}
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 pt-6">
        {/* FULL WIDTH like the room's stream (2026-10-14 — was a centred ``max-w-5xl``): the composer below is an
            exact copy of the room's, so the stream spans the same width it does. */}
        <div className="w-full">
          <div className={`${WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING} space-y-2.5`}>
          {/* Approval load error banner */}
          {approvalLoadError && onRetryApprovalLoad && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-sm text-amber-800">Could not load approval state. The backend may need to be deployed.</p>
              <button
                onClick={onRetryApprovalLoad}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Chat messages. The build deck is the single home for EVERY user
              prompt (kickoff + each revise) — so NO user message ever renders as
              a standalone bubble (single-deck goal). Only non-user messages
              (error/system) render here. */}
          {messages.map((message, idx) => (
            message.role === 'user' ? null : (
            <div
              key={`msg-${message.id || idx}`}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`
                  max-w-3xl
                  ${
                    message.role === 'user'
                      ? 'rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-800 shadow-sm'
                      : 'px-0 py-0 text-[0.9375rem] leading-[1.46668] text-gray-800'
                  }
                `}
              >
                <div className="whitespace-pre-wrap leading-normal">{message.content}</div>
                <div className="mt-1 text-[0.75rem] text-gray-500">
                  <time dateTime={message.timestamp || undefined} title={formatMessageTimeFull(message.timestamp)}>{formatMessageTime(message.timestamp)}</time>
                </div>
              </div>
            </div>
            )
          ))}

          {/* Stages — approval stage lives in the alert band, not here */}
          {stages.filter(s => {
            if (s.stage_id === 'processing') return false;
            if (s.stage_id === 'approval') return false;
            if (isProcessing && activeStage === s.stage_name) return false;
            return true;
          }).map((stage, idx) => (
            <StageRenderer
              key={stage.stage_id || stage.stage_name || `stage-${idx}`}
              stage={stage}
              isActive={activeStage === stage.stage_name}
            />
          ))}

          {/* Network diagram moved to the dedicated right-side panel in
              BuildTab (mirrors the Agents tab). No longer inlined here. */}

          {/* Build-activity panel. During a build it's shown as soon as ANY
              phase is set (DESIGNING / WRITING_PROMPTS / DRAFTING_SKILLS /
              FINALIZING) — not just when rows exist — so the architect head and
              the finalizing tail keep a live phase label + spinner instead of a
              dead gap. After the build (revisit), it persists as long as rows
              exist: the box is rehydrated from the AGENT rows (HYDRATE_BUILD) so
              prompts + consumption survive a reload, mirroring the room's feed.
              The plain spinner below is only the pre-first-event fallback. */}
          {buildPanelVisible ? (
            <BuildActivityPanel
              buildActivity={buildActivity}
              live={isProcessing || showApprovalBar}
              awaitingApproval={showApprovalBar}
              prompt={buildPrompt}
              networkId={network?.networkId}
              builds={network?.builds}
              teamsDiagram={network?.teamsArchitecture || ''}
              planKind={(network?.phases || []).length ? 'playbook'
                : (network?.suggestedPlan?.kind === 'teams' || network?.expertNetwork ? 'teams' : 'suggested')}
            />
          ) : null}

          <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* EXACT COPY of the room's composer frame (Composer.jsx): ``shrink-0 px-6 pb-2`` → ``w-full`` — full width,
          aligned with the wide stream; not a centred max-w column */}
      <div className="shrink-0 px-6 pb-2">
        <div className="w-full">
          {/* Composer: the room's CommandDeck (the same three decks as the room) */}
          <form onSubmit={handleSubmit} className="relative z-20">
            {/* THE ROOM'S THREE-DECK COMPOSER (MODES.md §9, CommandDeck — 2026-10-14; UI only, same handlers):
                upper deck = the "+" menu (attach files · skills) LEFT · middle = the prompt · lower deck = Send RIGHT
                (the room's Send) — the same frame, classes and sizes as the room. */}
            <CommandDeck
              testId="build-command-deck"
              alertBand={buildPromptAlertBand}
              topLeft={(<>
                {/* THE SHARED UPPER-LEFT GROUP (DeckLeftGroup — the room's, exactly); attaching to a build isn't wired
                    yet → Attach + </> greyed with why */}
                <DeckLeftGroup testId="build-composer" onFormat={formatInput} formatDisabled={inputDisabled}
                  attachSoon="Attaching files to a build — coming soon" snippetSoon="Code snippets in a build — coming soon" />
                {composerTopLeft || composerBottomLeft}
              </>)}
              topRight={composerTopRight}
              bottomLeft={composerLowerLeft}
              bottomRight={(
                <button
                  type="submit"
                  disabled={!inputValue.trim() || inputDisabled}
                  data-testid="build-composer-send"
                  className={`inline-flex h-7 w-8 shrink-0 items-center justify-center rounded font-medium transition-colors ${
                    !inputValue.trim() || inputDisabled
                      ? 'bg-transparent text-gray-300 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  aria-label="Send message"
                >
                  {inputDisabled && isProcessing ? (
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    // the room's Slack-style solid send arrow
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path d="M1.5 2.1a.75.75 0 0 1 .82-.14l16 7.35a.75.75 0 0 1 0 1.37l-16 7.35a.75.75 0 0 1-1.05-.86L3.1 10 1.27 2.93a.75.75 0 0 1 .23-.83ZM4.43 10.75l-1.25 4.86L15.6 10 3.18 4.39l1.25 4.86h5.82a.75.75 0 0 1 0 1.5H4.43Z" />
                    </svg>
                  )}
                </button>
              )}
            >
              <div className="relative flex flex-col gap-2">{/* the room's input deck: flex-col gap-2 */}
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={inputDisabled}
                  placeholder={placeholderText}
                  aria-label={placeholderText}
                  data-testid="build-composer-input"
                  className={getWorkbenchPromptInputClassName({ disabled: inputDisabled, size: 'deck' })}
                  style={WORKBENCH_PROMPT_FIELD_STYLE}
                />
              </div>
            </CommandDeck>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatWorkspace;
